import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConsentStatusItem } from '@/api/consents/consentsApi.types';
import { ClinicContactCard } from '@/components/client/ClientPortal/ClinicContactCard';
import { Icon } from '@/components/common/Icon';
import { SafeHtml } from '@/components/common/SafeHtml';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useOwnClinicContactQuery } from '@/hooks/useOwnClinicContactQuery';

interface ConsentsDeclinedScreenProps {
  declinedRequiredConsents: ConsentStatusItem[];
  onReview: () => void;
}

export function ConsentsDeclinedScreen({
  declinedRequiredConsents,
  onReview,
}: ConsentsDeclinedScreenProps) {
  const { t } = useTranslation('clients');
  const { logOut } = useAuth();
  const [isContactVisible, setIsContactVisible] = useState(false);
  const clinicContactQuery = useOwnClinicContactQuery();
  const clinicContact = clinicContactQuery.data;

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <Card as="section">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-9 h-9 rounded-xl bg-zooerrorbg text-zooerrortext flex items-center justify-center shrink-0">
            <Icon name="AlertTriangle" />
          </span>
          <h1 className="text-lg font-bold text-zooindigo">{t('portal.consentGate.title')}</h1>
        </div>
        <p className="text-sm text-zoomuted mb-4">{t('portal.consentGate.description')}</p>
        <ul className="space-y-3 mb-2">
          {declinedRequiredConsents.map(function renderConsent(item) {
            return (
              <li key={item.documentId} className="border border-zooborder rounded-xl p-3">
                <p className="text-sm font-bold text-zooindigo mb-1">{item.title}</p>
                <SafeHtml html={item.body} className="text-xs text-zoomuted" />
              </li>
            );
          })}
        </ul>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button size="large" className="sm:col-span-3" onClick={onReview}>
          {t('portal.consentGate.review')}
        </Button>
        <Button
          variant="outline"
          size="large"
          className="sm:col-span-2"
          onClick={function toggleContact() {
            setIsContactVisible(function toggle(previous) {
              return previous === false;
            });
          }}
        >
          {t('portal.consentGate.contactClinic')}
        </Button>
        <Button
          variant="destructiveGhost"
          size="large"
          onClick={function handleLogout() {
            logOut();
          }}
        >
          {t('portal.logout')}
        </Button>
      </div>

      {isContactVisible === true && clinicContact !== undefined ? (
        <ClinicContactCard clinicContact={clinicContact} />
      ) : null}
    </div>
  );
}
